import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  TextField,
  Button,
  Typography,
  Stack,
  CircularProgress,
  Box,
  Link,
  Fade,
  InputAdornment,
  IconButton,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { alpha, useTheme } from "@mui/material/styles";
import axios from "axios";
import { baseUrl } from "../utils/baseUrl";
import { login, setUserProfile } from "../Slices/roomNateSlice";
import { useDispatch } from "react-redux";
import { useGlobalAuthContext } from "../context/AuthContext";
import Cookies from "js-cookie";
import {
  getGuestLayoutUuids,
  clearGuestLayouts,
} from "../utils/guestLayoutStorage";
import { getGuestSessionId } from "../utils/guestSession";
import {
  DUPLICATE_EMAIL_MESSAGE,
  RATE_LIMITED_MESSAGE,
  isDuplicateEmailError,
  isRateLimitedError,
} from "../utils/signupErrors";
import { legalLinkSx, MF_PRIVACY_URL, MF_TERMS_URL } from "../utils/legalLinks";
import { getStandardDialogPaperSx } from "./StandardDialog";
import { getMFPrimaryButtonSx } from "../utils/dialogStyles";
import { getAuthGlassFieldSx } from "./Auth/authGlassStyles";

/**
 * Inline auth modal for the guest editor.
 * Default state: Sign up
 * Secondary state: Log in
 *
 * Activated from UploadFloor via requireAuth for guests (Save Layout, Share menu,
 * duplicate, download, gallery save, etc.).
 *
 * Uses axios directly instead of RTK Query's dologin/signup mutations to avoid
 * cache invalidation (dologin invalidates "savedLayouts" tag, which would
 * refetch layoutData and overwrite the guest's in-memory edits).
 */
export default function AuthModal({
  open,
  onClose,
  onSuccess,
  saving,
  initialEmail = "",
  // Phase 1.5: when saving=true and forkSaveError is non-null, the modal
  // swaps its "Saving your layout..." spinner for an error state with Retry
  // and Dismiss actions. Without this, a fork-save failure would leave the
  // user staring at an indefinite spinner because the AuthModal renders
  // above any sibling overlays via the MUI Dialog portal.
  forkSaveError,
  onRetryForkSave,
  onDismissForkSave,
}) {
  const theme = useTheme();
  const [mode, setMode] = useState("signup"); // "signup" | "login"
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPsw, setShowPsw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (!open) return;
    if (initialEmail) {
      setEmail(initialEmail);
      setMode("signup");
    }
  }, [open, initialEmail]);

  const reduxDispatch = useDispatch();
  const { dispatch: authDispatch } = useGlobalAuthContext();

  const textPrimary = theme.palette.text.primary;
  const textSecondary = theme.palette.text.secondary;
  const errorMain = theme.palette.error?.main ?? "#d32f2f";

  const { authModalPaperSx, glassFieldSx, backdropSx } = useMemo(() => {
    const paper = {
      ...getStandardDialogPaperSx(theme),
      backgroundColor: "#ffffff",
      maxWidth: "min(100%, 400px)",
      width: "100%",
      // Sides + bottom consistent; top clears the status bar on mobile so the
      // modal anchors near the top and doesn't jump when the keyboard opens.
      marginLeft: { xs: "16px", sm: "24px" },
      marginRight: { xs: "16px", sm: "24px" },
      marginBottom: { xs: "16px", sm: "24px" },
      marginTop: {
        xs: "max(env(safe-area-inset-top, 0px) + 2px, 14px)",
        sm: "14px",
      },
      position: "relative",
      p: 0,
    };

    // Inherit the shared glass field styles but increase the idle background so
    // fields are clearly distinguishable from the white modal surface.
    const baseFieldSx = getAuthGlassFieldSx(theme);
    const fields = {
      ...baseFieldSx,
      "& .MuiOutlinedInput-root": {
        ...baseFieldSx["& .MuiOutlinedInput-root"],
        backgroundColor: "rgba(0,0,0,0.08)",
      },
    };

    const backdrop = {
      backdropFilter: "none",
      WebkitBackdropFilter: "none",
      backgroundColor: alpha(theme.palette.common.black, 0.45),
    };

    return {
      authModalPaperSx: paper,
      glassFieldSx: fields,
      backdropSx: backdrop,
    };
  }, [theme]);

  const resetForm = () => {
    setName("");
    setEmail("");
    setPassword("");
    setShowPsw(false);
    setError("");
    setLoading(false);
  };

  React.useEffect(() => {
    if (!open) {
      resetForm();
      setMode("signup");
    }
  }, [open]);

  const switchMode = (newMode) => {
    setError("");
    setShowPsw(false);
    setMode(newMode);
  };

  const handleClose = () => {
    resetForm();
    setMode("signup");
    onClose?.();
  };

  const handleLogin = async () => {
    const guestUuids = getGuestLayoutUuids();
    const body = { email, password };
    if (guestUuids.length > 0) body.guestLayoutUuids = guestUuids;
    // Claim guest-session uploads (incl. early-exit uploads the uuid list never
    // captured). Both casings: snake for the auth-time claim, camel for the
    // backend's guestSessionId-keyed lookups.
    const guestSessionId = getGuestSessionId();
    if (guestSessionId) {
      body.guest_session_id = guestSessionId;
      body.guestSessionId = guestSessionId;
    }
    const response = await axios.post(`${baseUrl}/user/login`, body);
    const result = response?.data;
    const authToken = result?.token;
    const user = result?.user;

    if (!authToken) {
      setError("Login failed. Please try again.");
      return null;
    }

    if (user?.status === "blocked") {
      setError("This account has been blocked.");
      return null;
    }

    authDispatch({ type: "LOGIN", payload: authToken });
    reduxDispatch(login(authToken));
    if (user) {
      reduxDispatch(setUserProfile(user));
    }
    Cookies.set("token", JSON.stringify(authToken), { expires: 7 });
    if (guestUuids.length > 0) clearGuestLayouts(guestUuids);
    return user || null;
  };

  const handleSignup = async () => {
    const guestUuids = getGuestLayoutUuids();
    const body = { name, email, password, role: "user" };
    if (guestUuids.length > 0) body.guestLayoutUuids = guestUuids;
    // Claim guest-session uploads (incl. early-exit uploads the uuid list never
    // captured). Both casings: snake for the auth-time claim, camel so the
    // server-emitted account_created event picks up meta.guestSessionId.
    const guestSessionId = getGuestSessionId();
    if (guestSessionId) {
      body.guest_session_id = guestSessionId;
      body.guestSessionId = guestSessionId;
    }
    // This modal only opens over a staged floor plan (guest editor/gallery), so
    // the backend's authoritative account_created event is tagged "editor".
    body.signup_source = "editor";
    const response = await axios.post(`${baseUrl}/user/signup`, body);
    const result = response?.data;
    const authToken = result?.token;
    const user = result?.user;

    if (user?.status === "blocked") {
      setError("We'll email you as soon as your account is approved.");
      return null;
    }

    if (!authToken) {
      setError("Signup failed. Please try again.");
      return null;
    }

    authDispatch({ type: "LOGIN", payload: authToken });
    reduxDispatch(login(authToken));
    if (user) {
      reduxDispatch(setUserProfile(user));
    }
    Cookies.set("token", JSON.stringify(authToken), { expires: 7 });
    if (guestUuids.length > 0) clearGuestLayouts(guestUuids);
    return user || null;
  };

  // Lightweight client-side validation — mirrors the dedicated page Yup rules.
  const validate = () => {
    if (mode === "signup" && !name.trim())
      return "Please enter your full name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))
      return "Please enter a valid email address.";
    if (password.length < 8)
      return "Password must be at least 8 characters.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationMsg = validate();
    if (validationMsg) { setError(validationMsg); return; }
    setError("");
    setLoading(true);

    try {
      const authedUser = mode === "login" ? await handleLogin() : await handleSignup();
      if (authedUser) {
        onSuccess?.({
          isNewAccount: mode === "signup",
          email: email.trim(),
          name: name.trim(),
          // Pass the authenticated user id so callers can run an ownership
          // check before forking — owner returning to their own plan must
          // continue saving against the original, not a fresh copy.
          userId: authedUser?._id ? String(authedUser._id) : null,
        });
      } else {
        setLoading(false);
      }
    } catch (err) {
      setLoading(false);
      // A taken email isn't a generic failure — flip to log in (the email
      // state carries over, so the field stays filled) and say why.
      if (mode === "signup" && isDuplicateEmailError(err)) {
        setMode("login");
        setShowPsw(false);
        setError(DUPLICATE_EMAIL_MESSAGE);
        return;
      }
      // Throttled by the backend's per-IP signup limiter — stay on the form,
      // but say why instead of echoing the limiter's JSON body. Scoped to
      // signup because this catch is shared with login, which is not limited.
      if (mode === "signup" && isRateLimitedError(err)) {
        setError(RATE_LIMITED_MESSAGE);
        return;
      }
      const msg = err?.response?.data || err?.message || "Something went wrong. Please try again.";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    }
  };

  const isLogin = mode === "login";

  // Unified primary pill — brand gradient with auto-contrast label
  // (theme.palette.primary.contrastText). Same primitive as login / signup /
  // gallery inquire / editor Apply + Make Magic, so the entire app uses one
  // primary CTA recipe regardless of the surface it opens over.
  const ctaSx = {
    ...getMFPrimaryButtonSx(theme),
    "&.Mui-disabled": { color: "#ffffff", opacity: 0.55 },
  };

  // Shared link-as-button sx: prevent any inherited text-transform from
  // MUI's button reset applying capitalize/uppercase to the label.
  const linkTouchSx = {
    minHeight: 44,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 600,
    textTransform: "none !important",
    verticalAlign: "baseline",
  };

  // Shared password toggle adornment — implemented directly so the
  // show/hide state is owned by this component with no prop-threading risk.
  const passwordEndAdornment = (
    <InputAdornment position="end">
      <IconButton
        type="button"
        aria-label={showPsw ? "Hide password" : "Show password"}
        onClick={() => setShowPsw((v) => !v)}
        onMouseDown={(e) => e.preventDefault()}
        tabIndex={-1}
        edge="end"
        sx={{ width: 44, height: 44 }}
      >
        {showPsw ? (
          <VisibilityOffIcon sx={{ fontSize: 20 }} />
        ) : (
          <VisibilityIcon sx={{ fontSize: 20 }} />
        )}
      </IconButton>
    </InputAdornment>
  );

  // Header typography shared style
  const headerSx = {
    fontSize: { xs: "1.125rem", sm: "1.1875rem" },
    fontWeight: 600,
    letterSpacing: "-0.02em",
    color: textPrimary,
  };

  // Legal strip shared between both forms
  const legalStrip = (
    <Box
      sx={{
        color: "var(--color-text-tertiary)",
        textAlign: "center",
        pt: 0.5,
        width: "100%",
        fontSize: "10px",
      }}
    >
      <Box
        component="a"
        href={MF_PRIVACY_URL}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ ...legalLinkSx, fontSize: "10px" }}
      >
        Privacy
      </Box>
      {" · "}
      <Box
        component="a"
        href={MF_TERMS_URL}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ ...legalLinkSx, fontSize: "10px" }}
      >
        Terms
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : handleClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="auth-modal-title"
      aria-describedby="auth-modal-description"
      sx={{
        // Anchor near the top on mobile so the soft keyboard doesn't push
        // the modal off-screen; centred as normal on sm+.
        "& .MuiDialog-container": {
          alignItems: { xs: "flex-start", sm: "center" },
        },
      }}
      BackdropProps={{
        sx: backdropSx,
      }}
      PaperProps={{
        elevation: 0,
        sx: authModalPaperSx,
      }}
    >
      <DialogContent
        sx={{
          position: "relative",
          zIndex: 1,
          p: { xs: 2, sm: 2.5 },
          pt: { xs: "max(20px, env(safe-area-inset-top, 0px))", sm: 2.5 },
          pb: { xs: "max(20px, env(safe-area-inset-bottom, 0px))", sm: 2.5 },
        }}
      >
        {saving && forkSaveError ? (
          /* ─── Fork-save error state (Phase 1.5) ─── */
          <Fade in>
            <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ py: { xs: 4, sm: 5 } }}>
              <Typography
                id="auth-modal-title"
                component="h2"
                sx={{
                  fontSize: { xs: "20px", sm: "22px" },
                  fontWeight: 600,
                  letterSpacing: "-0.02em",
                  color: textPrimary,
                  textAlign: "center",
                }}
              >
                Save didn't go through
              </Typography>
              <Typography
                id="auth-modal-description"
                sx={{
                  fontSize: "15px",
                  lineHeight: 1.45,
                  color: textSecondary,
                  textAlign: "center",
                  maxWidth: 320,
                }}
              >
                {forkSaveError}
              </Typography>
              <Stack direction="row" spacing={1.5} sx={{ pt: 1 }}>
                <Button
                  variant="outlined"
                  onClick={onDismissForkSave}
                  sx={{
                    textTransform: "none",
                    minWidth: 110,
                    borderColor: "rgba(60,60,67,0.18)",
                    color: textPrimary,
                  }}
                >
                  Dismiss
                </Button>
                <Button
                  variant="contained"
                  onClick={onRetryForkSave}
                  sx={{ ...ctaSx, minWidth: 110 }}
                >
                  Retry
                </Button>
              </Stack>
            </Stack>
          </Fade>
        ) : saving ? (
          <Fade in>
            <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ py: { xs: 4, sm: 6 } }}>
              <CircularProgress size={40} color="primary" />
              <Typography
                id="auth-modal-title"
                component="h2"
                sx={{
                  fontSize: { xs: "20px", sm: "22px" },
                  fontWeight: 600,
                  letterSpacing: "-0.02em",
                  color: textPrimary,
                  textAlign: "center",
                }}
              >
                Saving your layout...
              </Typography>
              <Typography
                id="auth-modal-description"
                sx={{
                  fontSize: "15px",
                  lineHeight: 1.45,
                  color: textSecondary,
                  textAlign: "center",
                  maxWidth: 280,
                }}
              >
                This will only take a moment
              </Typography>
            </Stack>
          </Fade>
        ) : isLogin ? (
          /* ─── Login form ─── */
          <form onSubmit={handleSubmit} noValidate>
            <Typography id="auth-modal-title" component="h2" sx={headerSx}>
              Log in
            </Typography>

            <Stack spacing={1.75} sx={{ mt: "16px" }}>
              <TextField
                id="auth-email-login"
                name="email"
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                fullWidth
                autoFocus
                autoComplete="email"
                InputProps={{ style: { height: 48 } }}
                inputProps={{
                  inputMode: "email",
                  autoCapitalize: "none",
                  autoCorrect: "off",
                  spellCheck: false,
                  enterKeyHint: "next",
                }}
                sx={glassFieldSx}
              />

              <TextField
                id="auth-password-login"
                name="password"
                label="Password"
                type={showPsw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                fullWidth
                autoComplete="current-password"
                InputProps={{ style: { height: 48 }, endAdornment: passwordEndAdornment }}
                inputProps={{
                  enterKeyHint: "go",
                  autoCorrect: "off",
                  autoCapitalize: "none",
                  spellCheck: false,
                }}
                sx={glassFieldSx}
              />

              {error && (
                <Typography
                  id="auth-modal-error"
                  role="alert"
                  aria-live="polite"
                  sx={{ fontSize: "14px", lineHeight: 1.45, color: errorMain }}
                >
                  {error}
                </Typography>
              )}

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disableElevation
                disabled={loading || !email || !password}
                sx={ctaSx}
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
              >
                {loading ? "Signing in..." : "Log in"}
              </Button>

              <Typography
                sx={{
                  fontSize: "14px",
                  lineHeight: 1.45,
                  color: textSecondary,
                  textAlign: "center",
                  pt: 0.25,
                }}
              >
                New to MagicFurnish?{" "}
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  onClick={() => switchMode("signup")}
                  sx={{ ...linkTouchSx, color: textPrimary }}
                >
                  Create a free account
                </Link>
              </Typography>

              <Box sx={{ textAlign: "center" }}>
                <Link
                  href="/forgetPsw"
                  underline="hover"
                  sx={{
                    fontSize: "12px",
                    fontWeight: 400,
                    color: textSecondary,
                    display: "inline-flex",
                    alignItems: "center",
                    minHeight: 36,
                  }}
                >
                  Forgot password?
                </Link>
              </Box>

              {legalStrip}
            </Stack>
          </form>
        ) : (
          /* ─── Signup form ─── */
          <form onSubmit={handleSubmit} noValidate>
            <Typography id="auth-modal-title" component="h2" sx={headerSx}>
              Save and share your layout
            </Typography>

            <Stack spacing={1.75} sx={{ mt: "16px" }}>
              <TextField
                id="auth-name"
                name="name"
                label="Full name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                fullWidth
                autoFocus
                autoComplete="name"
                InputProps={{ style: { height: 48 } }}
                inputProps={{
                  autoCapitalize: "words",
                  autoCorrect: "off",
                  spellCheck: false,
                  enterKeyHint: "next",
                }}
                sx={glassFieldSx}
              />

              <TextField
                id="auth-email-signup"
                name="email"
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                fullWidth
                autoComplete="email"
                InputProps={{ style: { height: 48 } }}
                inputProps={{
                  inputMode: "email",
                  autoCapitalize: "none",
                  autoCorrect: "off",
                  spellCheck: false,
                  enterKeyHint: "next",
                }}
                sx={glassFieldSx}
              />

              <TextField
                id="auth-password-signup"
                name="password"
                label="Password"
                type={showPsw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                fullWidth
                autoComplete="new-password"
                InputProps={{ style: { height: 48 }, endAdornment: passwordEndAdornment }}
                inputProps={{
                  minLength: 8,
                  enterKeyHint: "go",
                  autoCorrect: "off",
                  autoCapitalize: "none",
                  spellCheck: false,
                }}
                sx={glassFieldSx}
              />

              {error && (
                <Typography
                  id="auth-modal-error"
                  role="alert"
                  aria-live="polite"
                  sx={{ fontSize: "14px", lineHeight: 1.45, color: errorMain }}
                >
                  {error}
                </Typography>
              )}

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disableElevation
                disabled={loading || !email || !password}
                sx={ctaSx}
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
              >
                {loading ? "Creating account..." : "Create free account"}
              </Button>

              <Typography
                sx={{
                  fontSize: "14px",
                  lineHeight: 1.45,
                  color: textSecondary,
                  textAlign: "center",
                  pt: 0.25,
                }}
              >
                Already have an account?{" "}
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  onClick={() => switchMode("login")}
                  sx={{ ...linkTouchSx, color: textPrimary }}
                >
                  Log in
                </Link>
              </Typography>

              {legalStrip}
            </Stack>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
